// src/admin/pages/LoginPage.jsx
import React, { useState } from 'react';
import PreAuthCard from '../../shared/components/PreAuthCard';
import {
  ADMIN_NETWORK_ERROR_MESSAGE,
  isTransientNetworkError,
} from '../auth/networkErrors';
import { useNavigate } from 'react-router-dom';
import AdminPreAuthLayout from '../layouts/AdminPreAuthLayout';
import FormInput from '../../shared/components/FormInput';
import IconButton from '../../shared/components/IconButton';
import { API_URL } from '../../config/env';

export default function LoginPage({ onLogin, onRegister }) {
  const [step, setStep] = useState('email'); // "email" or "password"
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendThrottled, setResendThrottled] = useState('');
  const navigate = useNavigate();

  // Handle next step after entering email
  const handleNext = (e) => {
    e.preventDefault();
    setError('');
    setResendMessage('');
    setResendThrottled('');
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setStep('password');
  };

  // Handle login submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendMessage('');
    setResendThrottled('');
    setSubmitting(true);
    try {
      await onLogin(email.trim(), password, navigate);
    } catch (err) {
      if (isTransientNetworkError(err)) {
        setError(ADMIN_NETWORK_ERROR_MESSAGE);
      } else if (
        err.message === 'Invalid credentials' ||
        err.status === 401 ||
        err.status === 400
      ) {
        setError('Invalid email or password');
      } else {
        setError(err.message || 'Sign-in failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Go back to email step
  const handleChangeEmail = (e) => {
    e.preventDefault();
    setPassword('');
    setError('');
    setResendMessage('');
    setResendThrottled('');
    setStep('email');
  };

  const handleResendConfirmation = async (e) => {
    e.preventDefault();
    setError('');
    setResendMessage('');
    setResendThrottled('');
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/v1/admin/users/confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: { email: email.trim() } }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setResendThrottled(data.error || 'Please wait before requesting another email.');
        return;
      }
      if (!res.ok) {
        setError('Failed to resend. Please try again.');
        return;
      }
      setResendMessage(
        data.message
          || 'If your email address exists in our database, you will receive an email with instructions for how to confirm your email address in a few minutes.'
      );
    } catch {
      setError('Failed to resend. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminPreAuthLayout>
        <PreAuthCard>
          <div className="text-center mb-3">
            <h1 className="fs-4 fw-semibold mb-1">
              {step === 'email' ? 'Welcome back' : 'Enter your password'}
            </h1>
            <p className="text-muted mb-0">
              {step === 'email'
                ? 'Enter your email to continue'
                : (
                  <>
                    Signing in as <span className="fw-semibold">{email}</span>{' '}
                    <a
                      href="/"
                      className="ms-1 small text-decoration-underline"
                      onClick={handleChangeEmail}
                      style={{ cursor: 'pointer' }}
                    >
                      Change
                    </a>
                  </>
                )}
            </p>
          </div>

          {error && (
            <div className="alert alert-danger py-2 small" role="alert">
              {error}
            </div>
          )}
          {resendMessage && (
            <div className="alert alert-success py-2 small" role="status">
              {resendMessage}
            </div>
          )}
          {resendThrottled && (
            <div className="alert alert-info py-2 small" role="status">
              {resendThrottled}
            </div>
          )}

          {step === 'email' ? (
            <form key={step} className="mt-4" onSubmit={handleNext} autoComplete="on">
              <FormInput
                id="email"
                name="email"
                type="email"
                label="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                icon="bi bi-envelope-fill"
                required
                disabled={submitting}
                autoComplete="username"
                inputClassName="py-3 rounded-3 border fw-medium"
              />
              <IconButton
                type="submit"
                className="btn btn-primary w-100 py-3 fw-bold"
                icon="bi-arrow-right"
                disabled={submitting}
              >
                Next
              </IconButton>
            </form>
          ) : (
            <form key={step} className="mt-4" onSubmit={handleSubmit} autoComplete="on">
              {/* Present username in same form as password so password managers can save/fill */}
              <input
                type="email"
                name="username"
                value={email}
                readOnly
                autoComplete="username"
                className="visually-hidden"
                tabIndex={-1}
                aria-hidden="true"
              />
              <FormInput
                id="password"
                name="password"
                type="password"
                label="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                icon="bi bi-lock-fill"
                required
                disabled={submitting}
                autoComplete="current-password"
                inputClassName="py-3 rounded-3 border fw-medium"
              />
              <IconButton
                type="submit"
                className="btn btn-primary w-100 py-3 fw-bold"
                icon="bi-box-arrow-in-right"
                disabled={submitting}
              >
                Login
              </IconButton>
              <div className="text-center mt-3 d-flex flex-column gap-1">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-underline small"
                  style={{ fontSize: '1em' }}
                  onClick={() => navigate('/admin/password/forgot', { state: { email } })}
                  disabled={submitting}
                >
                  Forgot password?
                </button>
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-underline small"
                  style={{ fontSize: '1em' }}
                  onClick={handleResendConfirmation}
                  disabled={submitting}
                >
                  Resend confirmation email
                </button>
              </div>
            </form>
          )}
        </PreAuthCard>
        {/* Register */}
        <div className="text-center mt-4">
          <span className="text-muted">
            Don&apos;t have an organization account?
          </span>
          <button
            type="button"
            className="btn btn-link p-0 align-baseline text-primary fw-semibold ms-1"
            style={{ textDecoration: 'none' }}
            onClick={onRegister}
          >
            Register your organization
            <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.95em' }} />
          </button>
        </div>
      </AdminPreAuthLayout>
  );
}
